/**
 * Telegram OSINT channel data source.
 * Fetches messages from 27 curated OSINT Telegram channels via the Bot API.
 * Requires VITE_TELEGRAM_BOT_TOKEN. Channels are defined in config/telegram-channels.ts.
 *
 * Channels are fetched in batches to avoid rate limits.
 * Messages are normalized into NormalizedEvents with category-based typing.
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent, Severity } from '../base-source';
import { TELEGRAM_CHANNELS, type TelegramChannelDef, type TelegramCategory } from '@/config/telegram-channels';
import { getApiBase } from '@/app/api-router';

const SOURCE_ID = 'telegram';

/** Max channels to fetch per cycle (round-robin). */
const CHANNELS_PER_CYCLE = 5;
let channelCycleOffset = 0;

function telegramCategoryToEventType(category: TelegramCategory): NormalizedEvent['type'] {
  switch (category) {
    case 'conflict': return 'conflict';
    case 'military': return 'military';
    case 'geopolitical': return 'political';
    case 'disaster': return 'disaster';
    case 'cyber': return 'cyber_incident';
    case 'maritime': return 'vessel_position';
    case 'aviation': return 'flight_position';
    case 'general': return 'social';
    default: return 'social';
  }
}

function reliabilityToSeverity(reliability: TelegramChannelDef['reliability']): Severity {
  switch (reliability) {
    case 'high': return 'medium';
    case 'medium': return 'low';
    case 'low': return 'info';
    default: return 'info';
  }
}

interface TelegramMessage {
  message_id: number;
  date: number;
  text?: string;
  caption?: string;
  chat?: {
    id: number;
    title?: string;
    username?: string;
  };
  forward_from_chat?: {
    id: number;
    title?: string;
    username?: string;
  };
}

interface TelegramUpdate {
  update_id: number;
  channel_post?: TelegramMessage;
  message?: TelegramMessage;
}

interface TelegramResponse {
  ok: boolean;
  result?: TelegramUpdate[];
  description?: string;
}

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

export const telegramSource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'Telegram OSINT',
    description: 'Curated OSINT Telegram channel aggregation (27 channels)',
    icon: 'message-circle',
    category: 'news',
    updateInterval: 120_000, // 2 min
    requiresApiKey: true,
    apiKeyUrl: 'https://core.telegram.org/bots#how-do-i-create-a-bot',
    dataLicense: 'Telegram Terms',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [
    {
      key: 'botToken',
      label: 'Telegram Bot Token',
      type: 'password',
      required: true,
      helpText: 'Create a bot via @BotFather and paste the token here',
    },
  ] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[Sentinel] Telegram OSINT source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
  },

  async fetch(): Promise<NormalizedEvent[]> {
    try {
      // Select a batch of channels to query this cycle
      const batch: TelegramChannelDef[] = [];
      for (let i = 0; i < CHANNELS_PER_CYCLE && i < TELEGRAM_CHANNELS.length; i++) {
        const idx = (channelCycleOffset + i) % TELEGRAM_CHANNELS.length;
        batch.push(TELEGRAM_CHANNELS[idx]);
      }
      channelCycleOffset = (channelCycleOffset + CHANNELS_PER_CYCLE) % TELEGRAM_CHANNELS.length;

      const base = getApiBase();

      const results = await Promise.allSettled(
        batch.map(async (channel) => {
          const proxyUrl = `${base}/api/data/telegram?channel=${encodeURIComponent(channel.handle)}&limit=10`;
          const response = await fetch(proxyUrl, { signal: AbortSignal.timeout(10_000) });

          if (!response.ok) {
            throw new Error(`Telegram proxy returned ${response.status} for ${channel.handle}`);
          }

          const data: TelegramResponse = await response.json();

          if (!data.ok || !data.result) {
            return [];
          }

          const events: NormalizedEvent[] = [];

          for (const update of data.result) {
            const msg = update.channel_post ?? update.message;
            if (!msg) continue;

            const text = msg.text ?? msg.caption ?? '';
            if (!text || text.length < 10) continue;

            const timestamp = new Date(msg.date * 1000).toISOString();

            events.push({
              id: `tg-${channel.id}-${msg.message_id}`,
              source: SOURCE_ID,
              timestamp,
              type: telegramCategoryToEventType(channel.category),
              title: text.slice(0, 200),
              description: text.length > 200 ? text : undefined,
              severity: reliabilityToSeverity(channel.reliability),
              tags: [
                'telegram',
                channel.category,
                channel.handle,
                `reliability-${channel.reliability}`,
                ...(channel.region ? [channel.region.toLowerCase().replace(/\s+/g, '-')] : []),
              ],
              links: [
                { url: `https://t.me/${channel.handle}/${msg.message_id}`, label: channel.name },
              ],
              raw: {
                channelId: channel.id,
                channelHandle: channel.handle,
                channelName: channel.name,
                messageId: msg.message_id,
                reliability: channel.reliability,
                category: channel.category,
                region: channel.region,
                language: channel.language,
                textLength: text.length,
              },
            });
          }

          return events;
        }),
      );

      const events: NormalizedEvent[] = [];
      for (const result of results) {
        if (result.status === 'fulfilled') {
          events.push(...result.value);
        }
      }

      status.state = 'active';
      status.lastFetch = new Date().toISOString();
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};
